/**
 * Credentials Request Card
 *
 * Secure entry point for LinkedIn credentials during account onboarding:
 * instead of typing a password into the chat (where it would be persisted
 * in the conversation history), the user opens a dialog whose submission
 * goes straight to POST /ai-chat/onboarding/credentials. The chat history
 * only ever sees a password-free placeholder.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { API_V1 } from '../../../API/APIs/APIv1';
import { useAIChat } from '../../../hooks/use-ai-chat';

export interface CredentialsRequestCardData {
  licenseId?: string;
  suggestedEmail?: string;
}

const PRODUCTS = [
  { value: 'linkedin_basic', label: 'LinkedIn Basic' },
  { value: 'sales_navigator', label: 'Sales Navigator' },
  { value: 'recruiter', label: 'Recruiter' },
];

const CredentialsRequestCard: React.FC<{ data: CredentialsRequestCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { currentConversationId } = useAIChat();

  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState(data.suggestedEmail || '');
  const [password, setPassword] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Germany');
  const [product, setProduct] = useState('linkedin_basic');

  const canSubmit = email.trim().length > 3 && password.length > 0 && city.trim().length > 1 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await API_V1.post('/ai-chat/onboarding/credentials', {
        conversationId: currentConversationId || undefined,
        email: email.trim(),
        password,
        city: city.trim(),
        country: country.trim() || undefined,
        product,
        licenseId: data.licenseId || undefined,
        locale: i18n.language,
      });
      setPassword('');
      setSubmitted(true);
      setOpen(false);
      // The endpoint appended password-free messages — refresh the history
      queryClient.invalidateQueries({ queryKey: ['ai-chat', 'history'] });
      queryClient.invalidateQueries({ queryKey: ['ai-chat', 'conversations'] });
    } catch (e: any) {
      setError(
        e?.response?.data?.error ||
          t('credentialsSubmitFailed', 'Übermittlung fehlgeschlagen — bitte Eingaben prüfen und erneut versuchen.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper className={classes.card} elevation={0}>
      <Box className={classes.header}>
        {submitted ? <CheckCircleIcon color="success" fontSize="small" /> : <LockIcon className={classes.lockIcon} fontSize="small" />}
        <Typography variant="subtitle2" className={classes.title}>
          {submitted
            ? t('credentialsSubmittedTitle', 'Zugangsdaten übermittelt')
            : t('credentialsRequestTitle', 'LinkedIn-Zugangsdaten')}
        </Typography>
      </Box>

      {!submitted && (
        <>
          <Typography variant="body2" color="textSecondary">
            {t(
              'credentialsRequestHint',
              'Deine Zugangsdaten werden über ein sicheres Formular übermittelt und erscheinen nicht im Chatverlauf.'
            )}
          </Typography>
          <Button variant="contained" size="small" startIcon={<LockIcon />} onClick={() => setOpen(true)} className={classes.openButton}>
            {t('credentialsOpenForm', 'Zugangsdaten sicher eingeben')}
          </Button>
        </>
      )}
      {submitted && (
        <Typography variant="body2" color="textSecondary">
          {t('credentialsSubmittedHint', 'Der LinkedIn-Login läuft im Hintergrund — der Status erscheint hier im Chat.')}
        </Typography>
      )}

      <Dialog open={open} onClose={() => !submitting && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('credentialsDialogTitle', 'LinkedIn-Account verbinden')}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('credentialsEmail', 'LinkedIn E-Mail')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
            autoComplete="username"
          />
          <TextField
            label={t('credentialsPassword', 'LinkedIn Passwort')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            size="small"
            autoComplete="current-password"
          />
          <TextField
            label={t('credentialsCountry', 'Land (Proxy)')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label={t('credentialsCity', 'Stadt (Proxy — wo du arbeitest)')}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            select
            label={t('credentialsProduct', 'LinkedIn-Produkt')}
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            fullWidth
            size="small"
          >
            {PRODUCTS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary">
            {t('credentialsSecurityNote', '🔒 Die Daten gehen direkt an den Login-Dienst und werden nicht im Chat gespeichert.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={submitting}>
            {t('cancel', 'Abbrechen')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={14} /> : <LockIcon />}>
            {t('credentialsSubmit', 'Sicher übermitteln')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.main}33`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 480,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  lockIcon: {
    color: theme.palette.primary.main,
  },
  title: {
    fontWeight: 600,
  },
  openButton: {
    alignSelf: 'flex-start',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingTop: `${theme.spacing(1)} !important`,
  },
}));

export default CredentialsRequestCard;
